import type { Insurance } from "../../../__generated__/base.gql";

/**
 * Extracts the insurance number for display purposes.
 *
 * This function handles the legacy ASHIA insurance case where the insurance number
 * was stored as 'registrationNumber' in insuranceDetails instead of the standard
 * 'insuranceNumber' field used by other insurance providers.
 *
 * @param insurance - The insurance object from GraphQL
 * @returns The insurance number string for display, or null if not available
 */
export function getInsuranceNumberForDisplay(
  insurance:
    | Pick<Insurance, "insuranceNumber" | "insuranceDetails" | "insuranceType">
    | null
    | undefined,
): string | null {
  if (!insurance) {
    return null;
  }

  // First, try the standard insuranceNumber field (used by most insurers)
  if (insurance.insuranceNumber) {
    return insurance.insuranceNumber;
  }

  // For ASHIA insurance, check the legacy registrationNumber field in insuranceDetails
  if (insurance.insuranceType === "ASHIA" && insurance.insuranceDetails) {
    try {
      const details = insurance.insuranceDetails as Record<string, unknown>;
      const registrationNumber = details.registrationNumber;

      if (typeof registrationNumber === "string" && registrationNumber.trim()) {
        return registrationNumber;
      }
    } catch (error) {
      // If insuranceDetails is not a valid object, fall through to return null
      console.warn(
        "Failed to parse insurance details for ASHIA insurance:",
        error,
      );
    }
  }

  return null;
}

/**
 * Extracts the insurance number for display from invoice insurance details.
 *
 * This handles the case where insurance details come from invoice payers rather
 * than the patient's insurance profile. Used in printable invoices and similar contexts.
 *
 * @param insuranceDetails - The insurance details object from invoice/payer context
 * @param insuranceType - The insurance type (e.g., "ASHIA", "KADCHMA")
 * @returns The insurance number string for display, or null if not available
 */
export function getInsuranceNumberFromInvoiceDetails(
  insuranceDetails: Record<string, unknown> | null | undefined,
  insuranceType?: string | null,
): string | null {
  if (!insuranceDetails) {
    return null;
  }

  // First, try standard fields (membershipNumber, then insuranceNumber)
  const membershipNumber = insuranceDetails.membershipNumber;
  if (typeof membershipNumber === "string" && membershipNumber.trim()) {
    return membershipNumber;
  }

  const insuranceNumber = insuranceDetails.insuranceNumber;
  if (typeof insuranceNumber === "string" && insuranceNumber.trim()) {
    return insuranceNumber;
  }

  // For ASHIA insurance, check the legacy registrationNumber field
  if (insuranceType === "ASHIA") {
    const registrationNumber = insuranceDetails.registrationNumber;
    if (typeof registrationNumber === "string" && registrationNumber.trim()) {
      return registrationNumber;
    }
  }

  return null;
}
